import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  ShoppingCart, 
  FileText, 
  DollarSign, 
  Users, 
  Package,
  Clock,
  FilePlus,
  CheckCircle,
  Kanban,
  ArrowRight
} from "lucide-react";

export default function DashboardPage() {
  const { user, isAdmin, isCustomer } = useAuth();
  const [stats, setStats] = useState(null);
  const [customerSummary, setCustomerSummary] = useState({ in_queue: 0, active_orders: 0 });
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ordersRes, quotesRes, extraRes] = await Promise.all([
          api.get("/orders?page=1&per_page=5"),
          api.get("/quotations?page=1&per_page=5"),
          isAdmin() ? api.get("/analytics/dashboard") : api.get("/analytics/customer-summary"),
        ]);
        setRecentOrders(ordersRes.data.items || ordersRes.data);
        setRecentQuotes(quotesRes.data.items || quotesRes.data);
        if (isAdmin()) setStats(extraRes.data);
        else setCustomerSummary(extraRes.data);
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isAdmin]);

  const unconfirmedOrdersCount = customerSummary.in_queue;
  const confirmedOrdersCount = customerSummary.active_orders;

  const statusColors = {
    order_confirmed: "bg-blue-100 text-blue-800 border-blue-200",
    material_planning: "bg-purple-100 text-purple-800 border-purple-200",
    cutting_scheduled: "bg-indigo-100 text-indigo-800 border-indigo-200",
    cutting_in_progress: "bg-yellow-100 text-yellow-800 border-yellow-200",
    qc_check: "bg-orange-100 text-orange-800 border-orange-200",
    packed: "bg-teal-100 text-teal-800 border-teal-200",
    dispatched: "bg-cyan-100 text-cyan-800 border-cyan-200",
    delivered: "bg-green-100 text-green-800 border-green-200",
    draft: "bg-slate-100 text-slate-800 border-slate-200",
    pending_approval: "bg-blue-100 text-blue-800 border-blue-200",
    approved: "bg-green-100 text-green-800 border-green-200",
    rejected: "bg-red-100 text-red-800 border-red-200",
    converted: "bg-emerald-100 text-emerald-800 border-emerald-200"
  };

  const getStatusLabel = (status) => {
    const labels = {
      order_confirmed: "Confirmed",
      material_planning: "Planning",
      cutting_scheduled: "Scheduled",
      cutting_in_progress: "Cutting",
      qc_check: "QC Check",
      packed: "Packed",
      dispatched: "Dispatched",
      delivered: "Delivered",
      draft: "Draft",
      pending_approval: "Pending",
      approved: "Approved",
      rejected: "Rejected",
      converted: "Converted"
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-slide-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="border-b border-slate-200 pb-6">
        {isCustomer() && (
          <div className="text-center mb-4">
            <p className="text-sm text-slate-400 uppercase tracking-widest mb-1">Welcome back</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-black text-slate-900 uppercase tracking-tight" data-testid="customer-company-name">
              {user?.company_name || user?.full_name}
            </h1>
          </div>
        )}
        {isAdmin() && (
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900 uppercase tracking-tight" data-testid="dashboard-title">
              Dashboard
            </h1>
            <p className="text-slate-500 mt-1">Welcome back, {user?.full_name}</p>
          </div>
        )}
      </div>

      {/* Customer Quick Action Cards */}
      {isCustomer() && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Create Order Card */}
          <Link to="/dashboard/create-order" className="group" data-testid="create-order-card">
            <Card className="card-industrial h-full border-2 border-transparent hover:border-orange-500 transition-all">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="w-16 h-16 bg-orange-500 flex items-center justify-center">
                    <FilePlus className="w-8 h-8 text-white" />
                  </div>
                  <ArrowRight className="w-6 h-6 text-slate-300 group-hover:text-orange-500 transition-colors" />
                </div>
                <h3 className="font-heading text-xl font-bold uppercase tracking-wider mt-6">
                  Create Order
                </h3>
                <p className="text-sm text-slate-500 mt-2">
                  Select products, upload designs, and get instant quotation
                </p>
              </CardContent>
            </Card>
          </Link>

          {/* Pending Orders Card */}
          <Link to="/dashboard/quotations" className="group" data-testid="pending-orders-card">
            <Card className="card-industrial h-full border-2 border-transparent hover:border-blue-500 transition-all">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="w-16 h-16 bg-blue-600 flex items-center justify-center">
                    <FileText className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-right">
                    <p className="text-4xl font-bold font-mono text-blue-600">{unconfirmedOrdersCount}</p>
                    <p className="text-xs text-slate-500 uppercase">Pending</p>
                  </div>
                </div>
                <h3 className="font-heading text-xl font-bold uppercase tracking-wider mt-6">
                  Pending Orders
                </h3>
                <p className="text-sm text-slate-500 mt-2">
                  Orders awaiting approval or confirmation
                </p>
              </CardContent>
            </Card>
          </Link>

          {/* Confirmed Orders Card */}
          <Link to="/dashboard/orders" className="group" data-testid="confirmed-orders-card">
            <Card className="card-industrial h-full border-2 border-transparent hover:border-green-500 transition-all">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="w-16 h-16 bg-green-600 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-right">
                    <p className="text-4xl font-bold font-mono text-green-600">{confirmedOrdersCount}</p>
                    <p className="text-xs text-slate-500 uppercase">Active</p>
                  </div>
                </div>
                <h3 className="font-heading text-xl font-bold uppercase tracking-wider mt-6">
                  Confirmed Orders
                </h3>
                <p className="text-sm text-slate-500 mt-2">
                  Track your orders in production
                </p>
              </CardContent>
            </Card>
          </Link>
        </div>
      )}

      {/* Stats Grid - Admin Only */}
      {isAdmin() && stats && (
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
          <Link to="/dashboard/orders" data-testid="stat-total-orders">
            <Card className="card-industrial hover:shadow-md hover:border-slate-400 transition-all cursor-pointer">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-slate-900 flex items-center justify-center">
                    <ShoppingCart className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold font-mono">{stats.total_orders}</p>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Total Orders</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/dashboard/orders" data-testid="stat-pending-orders">
            <Card className="card-industrial hover:shadow-md hover:border-orange-400 transition-all cursor-pointer">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-orange-500 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold font-mono">{stats.pending_orders}</p>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">In Queue</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/dashboard/analytics" data-testid="stat-revenue">
            <Card className="card-industrial hover:shadow-md hover:border-green-400 transition-all cursor-pointer">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-600 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold font-mono">₹{(stats.total_revenue / 1000).toFixed(1)}K</p>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Revenue</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/dashboard/customers" data-testid="stat-customers">
            <Card className="card-industrial hover:shadow-md hover:border-purple-400 transition-all cursor-pointer">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-600 flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold font-mono">{stats.total_customers}</p>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Customers</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/dashboard/products" data-testid="stat-products">
            <Card className="card-industrial hover:shadow-md hover:border-teal-400 transition-all cursor-pointer">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-teal-600 flex items-center justify-center">
                    <Package className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold font-mono">{stats.total_products}</p>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Products</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      )}

      {/* Recent Activity Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Orders / Confirmed Orders */}
        <Card className="card-industrial" data-testid="recent-orders-card">
          <CardHeader className="border-b border-slate-200">
            <Link to="/dashboard/orders">
              <CardTitle className="font-heading text-lg uppercase tracking-wider flex items-center justify-between hover:text-orange-600 transition-colors cursor-pointer">
                <span className="flex items-center gap-2"><CheckCircle size={18} />{isCustomer() ? "Confirmed Orders" : "Recent Orders"}</span>
                <ArrowRight size={16} className="text-slate-400" />
              </CardTitle>
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            {recentOrders.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-20" />
                <p>No orders yet</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {recentOrders.map((order) => (
                  <Link key={order.id} to="/dashboard/orders" className="block p-4 hover:bg-slate-50 transition-colors" data-testid={`order-${order.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-mono text-sm font-medium">{order.order_number}</p>
                        <p className="text-xs text-slate-500 mt-1">{order.items?.length || 0} item(s)</p>
                      </div>
                      <div className="text-right">
                        <Badge className={`badge-status ${statusColors[order.status]}`}>
                          {getStatusLabel(order.status)}
                        </Badge>
                        <p className="text-sm font-mono mt-1">₹{order.total?.toLocaleString()}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Quotations / Pending Orders */}
        <Card className="card-industrial" data-testid="recent-quotes-card">
          <CardHeader className="border-b border-slate-200">
            <Link to="/dashboard/quotations">
              <CardTitle className="font-heading text-lg uppercase tracking-wider flex items-center justify-between hover:text-orange-600 transition-colors cursor-pointer">
                <span className="flex items-center gap-2"><FileText size={18} />Pending Orders</span>
                <ArrowRight size={16} className="text-slate-400" />
              </CardTitle>
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            {recentQuotes.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-20" />
                <p>No quotations yet</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {recentQuotes.map((quote) => (
                  <Link key={quote.id} to="/dashboard/quotations" className="block p-4 hover:bg-slate-50 transition-colors" data-testid={`quote-${quote.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-mono text-sm font-medium">{quote.quote_number}</p>
                        <p className="text-xs text-slate-500 mt-1">{quote.items?.length || 0} item(s)</p>
                      </div>
                      <div className="text-right">
                        <Badge className={`badge-status ${statusColors[quote.status]}`}>
                          {getStatusLabel(quote.status)}
                        </Badge>
                        <p className="text-sm font-mono mt-1">₹{quote.total?.toLocaleString()}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
